/**
 * 把 html2canvas 截出的整张长图按 A4 分页写入 jsPDF。
 *
 * 旧做法按固定页高切图，切线落在文字行中间时，一行字被劈成上下两半分在两页，
 * 表现为「第一页与第二页中间文字不全」。这里在切线上方一段范围内找空白行
 * （行间距、段落间距），从空白处切；找不到时才退回固定位置切。
 */

// 一行像素与该行首像素色差超过 TOLERANCE 计为「有内容」；
// 允许最多 MAX_INK 个这样的像素，容纳表格/卡片的竖向边框线
const TOLERANCE = 24;
const MAX_INK = 12;
// 连续空白行至少 MIN_GAP 行才认为是行间空隙（排除字形上下边缘那一两行稀疏像素）
const MIN_GAP = 4;
// 最多向上回退页高的 30% 寻找空白
const SEARCH_RATIO = 0.3;

function isBlankRow(data) {
  const r0 = data[0];
  const g0 = data[1];
  const b0 = data[2];
  let ink = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (Math.abs(data[i] - r0) + Math.abs(data[i + 1] - g0) + Math.abs(data[i + 2] - b0) > TOLERANCE) {
      ink += 1;
      if (ink > MAX_INK) return false;
    }
  }
  return true;
}

/** 在 (minY, targetY] 内自下而上找一段连续空白行，返回其中点；找不到返回 targetY */
function findCutY(ctx, width, minY, targetY) {
  let run = 0;
  for (let y = targetY; y > minY; y--) {
    const row = ctx.getImageData(0, y, width, 1).data;
    if (isBlankRow(row)) {
      run += 1;
      if (run >= MIN_GAP) return y + Math.floor(run / 2);
    } else {
      run = 0;
    }
  }
  return targetY;
}

/**
 * @param {import('jspdf').jsPDF} pdf     已创建的 A4 纵向 jsPDF（单位 mm）
 * @param {HTMLCanvasElement} canvas      html2canvas 输出
 * @param {object} [opts]
 * @param {number} [opts.margin=5]        上下边距 mm
 * @param {number} [opts.quality=0.75]    JPEG 质量
 */
export function addCanvasToPdf(pdf, canvas, { margin = 5, quality = 0.75 } = {}) {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = canvas.width;
  const imgHeight = canvas.height;
  const ratio = pdfWidth / imgWidth; // 源图 px → mm
  const pagePx = Math.floor((pdfHeight - margin * 2) / ratio); // 每页可容纳的源图高度
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  let y = 0;
  let first = true;
  while (y < imgHeight) {
    let end = Math.min(y + pagePx, imgHeight);
    if (end < imgHeight) {
      const minY = Math.max(y, end - Math.floor(pagePx * SEARCH_RATIO));
      end = findCutY(ctx, imgWidth, minY, end);
    }
    const sliceH = end - y;

    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = imgWidth;
    pageCanvas.height = sliceH;
    const pctx = pageCanvas.getContext('2d');
    pctx.fillStyle = '#ffffff';
    pctx.fillRect(0, 0, imgWidth, sliceH);
    pctx.drawImage(canvas, 0, y, imgWidth, sliceH, 0, 0, imgWidth, sliceH);

    if (!first) pdf.addPage();
    pdf.addImage(pageCanvas.toDataURL('image/jpeg', quality), 'JPEG', 0, margin, pdfWidth, sliceH * ratio);
    first = false;
    y = end;
  }
}
